/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, createRef, useEffect, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import fetchHelper from "../services/fetchHelper";
import Loading from "./Loading";
import savingspinner from "../assets/esign-ui-icons/saving-ico-white.svg";
import PatientIcon from "../components/icons/PatientIcon";
import MimeTypes from "../constants/MimeTypes";
import dayjs from "dayjs";
import { dateFormats } from "../constants/DateFormatConstants";
import { REQUEST_TYPE_PDF } from "../components/Modals/constants";
import { useToasts } from "react-toast-notifications";
import { getSafeIrFilename } from "../utils/helpers";
import { LayoutContext } from "../components/layout";
import PatientSyncStatusIcon from "../components/PatientSync/SyncStatusIcon";
import usePatientSyncHandler from "../components/PatientSync/useSyncHandler";
import "./UserInfoCard.scss";
import { findConclusionStatementsFromIds, getAnticoagulantStatementV2 } from "../utils/reportStatementsUtil";
import { AnticoagulantIcon, AnticoagulantText } from "../constants/InterrogationConstans";

const InterrogationStatus = ({ value }) => {
  return (
    <div id="status-label" className="status-label">
      <b>{value === "TERMINATED" ? "REMOVED" : value}</b>
    </div>
  );
};

export const EditVisitNumber = ({ value, interrogationId }) => {
  const [visitNumber, setVisitNumber] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => setVisitNumber(value), [value]);

  const onSave = async () => {
    try {
      setIsLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/interrogations/${interrogationId}`, {
        visitNumber,
        id: interrogationId,
      });
      setEditMode(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  return (
    <>
      {editMode ? (
        <div style={{ display: "flex", alignItems: "center" }}>
          Visit#:
          <input
            id="visit-number-text-field"
            data-testid="visit-number-field"
            type="text"
            value={visitNumber?.trim()}
            onChange={(e) => {
              setVisitNumber(e.target.value);
            }}
            style={{ minWidth: 150, height: 38, marginTop: 0, marginRight: 3, boxShadow: "none" }}
          />
          <button
            id="save-visit-number"
            className="btn btnSuccess"
            style={{ padding: 0, height: 36, width: 37 }}
            onClick={onSave}
          >
            {isLoading ? (
              <div
                id="results-loading-icon"
                className="saving-white-ico icon-64"
                style={{ width: 15, height: 15, marginRight: 3 }}
              />
            ) : (
              <div className="check-flat-ico icon-64" style={{ width: 15, height: 20 }} />
            )}
          </button>
          <button
            id="cancel-visit-number"
            className="btn btn-urgent"
            style={{ padding: 0, height: 36, width: 37 }}
            onClick={() => {
              setVisitNumber(value);
              setEditMode(false);
            }}
          >
            <div className="close-flat-ico icon-64" style={{ width: 17, height: 17 }} />
          </button>
        </div>
      ) : (
        <div id="visit-number" style={{ display: "flex", minWidth: 120 }}>
          Visit#: {visitNumber}{" "}
          <div
            id="edit-visit-number-pencil-icon"
            className="icon-64 pencil-gray-icon"
            style={{ display: "inherit", cursor: "pointer" }}
            onClick={() => setEditMode(true)}
          />
        </div>
      )}
    </>
  );
};

const AssignerInfo = ({ assignerName, assignerFamily, assignerDate }) => {
  const [assignerNameText, setAssignerNameText] = useState("");
  const [assignerFamilyText, setAssignerFamilyText] = useState("");
  const [assignerDateText, setAssignerDateText] = useState("");

  useEffect(() => setAssignerNameText(assignerName), [assignerName]);
  useEffect(() => setAssignerFamilyText(assignerFamily), [assignerFamily]);
  useEffect(() => setAssignerDateText(assignerDate), [assignerDate]);

  return (
    <>
      {assignerNameText && assignerFamilyText && assignerDateText && (
        <p className="assigner-date-updated">
          Assigned by {assignerNameText + " " + assignerFamilyText} <br></br>
          {dayjs(assignerDateText).format(dateFormats.DATE_AND_TIME_DEFAULT)} UTC
        </p>
      )}
    </>
  );
};

const UserInfoCard = ({ userInfo, headerInfo, showDownload, showCycles }) => {
  const history = useHistory();
  const { currentLocation, assignerInfo, loadAssignerInfo } = useContext(LayoutContext);
  const [disableAfterRegenerate, setDisableAfterRegenerate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nurseFollowUp, setNurseFollowUp] = useState(false);
  const [nurseFollowUpLoading, setNurseFollowUpLoading] = useState(false);
  const followUpCheckBox = createRef();
  const [referringPhysician, setReferringPhysician] = useState(null);

  const refUrl = useHistory().location.pathname;
  const [downloadingPreReport, setDownloadingPreReport] = useState(false);
  const { i } = useParams();
  const { addToast } = useToasts();
  const { syncStatus, syncResult, dispatchSync, isSyncSupported } = usePatientSyncHandler();
  const [anticoagulantIconInfo, setAnticoagulantIconInfo] = useState({
    anticoagulantTooltip: "",
    anticoagulantText: "",
    anticoagulantIcon: "",
  });

  const fetchReferringPhysician = async () => {
    try {
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/v2/patients/" + headerInfo.patient.id + "/referringPhysician",
      });
      setReferringPhysician(data);
    } catch (err) {
      console.log(err);
    }
  };

  const loadAnticoagulantStatementByPatient = async (patientId) => {
    const statements = await getAnticoagulantStatementV2(patientId);
    const ids = statements.userSelectedStatements;
    if (!ids || ids.length === 0) return {};

    const anticoagulantTooltip = Object.values(findConclusionStatementsFromIds(ids)).join("<br>");
    const aacAptIdList = [4045, 4050, 4052];
    const aacAptIdFound = aacAptIdList.find((id) => ids.includes(id));
    const anticoagulantText = aacAptIdFound
      ? aacAptIdFound === 4045
        ? AnticoagulantText.APT
        : AnticoagulantText.LAAC
      : "";
    let anticoagulantIcon = AnticoagulantIcon.FATINT;
    if (headerInfo.patient.anticoagulant === "NO") {
      anticoagulantIcon = anticoagulantText ? AnticoagulantIcon.AACAPTTEXT : AnticoagulantIcon.FATINTSLASH;
    }

    setAnticoagulantIconInfo({ anticoagulantTooltip, anticoagulantIcon, anticoagulantText });
  };

  useEffect(() => {
    fetchHelper.post(`/patientmanagement/rest/interrogationAccessHistory/${i}`);
  }, []);

  useEffect(() => {
    if (headerInfo?.id) {
      loadAssignerInfo(headerInfo.id);
      fetchReferringPhysician();
    }
    if (headerInfo?.patient?.id) {
      loadAnticoagulantStatementByPatient(headerInfo.patient.id);
    }
  }, [headerInfo?.id || ""]);

  useEffect(() => {
    if (headerInfo) {
      setNurseFollowUp(headerInfo.nurseFollowUp);
    }
  }, [headerInfo]);

  useEffect(() => {
    if (headerInfo && isSyncSupported) dispatchSync(headerInfo.patient);
  }, [headerInfo, currentLocation]);

  if (!headerInfo) return <Loading count={3} hasHeader={false} style={{ padding: 0 }} />;
  headerInfo.patient = headerInfo.patient || {};
  headerInfo.device = headerInfo.device || {};

  const {
    patient: pt,
    device: dv,
    patient: { followingPhysician: fp },
  } = headerInfo;

  const age = () => {
    if (pt.dob) {
      var ageDifMs = Date.now() - new Date(pt.dob).getTime();
      var ageDate = new Date(ageDifMs);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    }
  };

  const dateDisplay = (data) => {
    const date = new Date(data);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const downloadFile = (url) => {
    setDownloadingPreReport(true);
    fetchHelper.getFile({ url: url, headers: { "Content-Type": MimeTypes.PDF } }).then((blob) => {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const filename = getSafeIrFilename(pt, i);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${filename}.pdf`);
      link.style.display = "none";

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode.removeChild(link);
      setDownloadingPreReport(false);
    });
  };

  const downloadReport = async () => {
    const pdfQueueMicroservice = await fetchHelper.getPdfQueueStatus(headerInfo.location.id);
    let extraParams = pdfQueueMicroservice ? `/${REQUEST_TYPE_PDF.FINAL}` : "";
    downloadFile(`/patientmanagement/rest/interrogations/${headerInfo.id}/download${extraParams}`);
  };

  const regenerateReport = async () => {
    try {
      setIsLoading(true);
      const lS = window.localStorage.getItem("genToken");
      const url = `/patientmanagement/rest/interrogations/${headerInfo.id}/regenerate`;
      await fetch(url, {
        method: "PUT",
        headers: {
          token: JSON.parse(lS)["token"],
          "Content-Type": "application/json",
        },
        body: {},
      });
      setIsLoading(false);
      setDisableAfterRegenerate(true);
    } catch (error) {
      setIsLoading(false);
      console.log("ERROR", error);
    }
  };

  const onFollowUpChange = async () => {
    try {
      setNurseFollowUpLoading(true);
      const lS = window.localStorage.getItem("genToken");
      let url = "/patientmanagement/rest/interrogations/" + headerInfo.id;
      await fetch(url, {
        method: "PUT",
        headers: {
          token: JSON.parse(lS)["token"],
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nurseFollowUp: !nurseFollowUp,
          id: headerInfo.id,
        }),
      });
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    } finally {
      setNurseFollowUpLoading(false);
      loadAssignerInfo(headerInfo.id);
    }
  };

  const displayUserDetail = (id) => {
    localStorage.removeItem("master-location");
    localStorage.setItem("master-location", refUrl);
    history.push(`/patients3/${id}`);
  };

  const timeZone = (timestamp) =>
    new Date(timestamp)
      .toLocaleTimeString("en-us", { hourCycle: "h23", timeZoneName: "short" })
      .split(" ")[0];

  return (
    <>
      <div className="col col-m-100 col-50">
        <div className="m-mfr-data-sb" id="patient-details">
          <div className="row">
            <div className="mfr-data-sb-prom" id="patient-name">
              <div onClick={() => displayUserDetail(pt.id)} style={{ color: "#4a4a4a", cursor: "pointer" }}>
                <b>{pt.name + " " + pt.family}</b>
              </div>
              {isSyncSupported && <PatientSyncStatusIcon status={syncStatus} date={syncResult?.updatedAt} />}
            </div>
            <div className="pt-header-info">
              <span style={{ display: "flex", alignItems: "center" }}>
                <span className="mr-2" id="patient-age">
                  Age:<b>{age()}</b>
                </span>
                <span className="mr-2" id="patient-dob">
                  DOB:<b>{pt.dob ? dayjs(pt.dob).format("MM/DD/YYYY") : "--"}</b>
                </span>
                <span className="mr-2" id="patient-mrn">
                  MRN:<b>{headerInfo.patient.patientLocation?.[0]?.mrn}</b>
                </span>
                <EditVisitNumber value={headerInfo.visitNumber} interrogationId={headerInfo.id} />
              </span>
            </div>
          </div>
          <div className="row">
            <div className="pt-hcol" id="dateId">
              Date:
              <b id="interrogationDateId">
                {headerInfo.interrogationDateAsString ? `${headerInfo.interrogationDateAsString}` : "--"}
              </b>{" "}
              Uploaded:
              <b id="uploadDateId">
                {headerInfo.uploadDate
                  ? `${new Date(headerInfo.uploadDate).toLocaleDateString()} - ${timeZone(
                    headerInfo.uploadDate
                  )}`
                  : "--"}
              </b>{" "}
              Reason:
              <b id="interrogationReasonId">{headerInfo.interrogationReason}</b>
            </div>
          </div>
          <div className="row">
            <div className="pt-hcol" onClick={showCycles} style={{ cursor: "pointer" }}>
              30:<strong>{headerInfo.thirtyDayBillStatus ?? "NA"}</strong>
              {headerInfo.thirtyDayBillStatus
                ? `(${dateDisplay(headerInfo.thirthyCycleStart)}~${dateDisplay(headerInfo.thirthyCycleEnd)})`
                : null}{" "}
              / 90:<strong>{headerInfo.nintyDayBillStatus ?? "NA"}</strong>
              {headerInfo.nintyDayBillStatus
                ? `(${dateDisplay(headerInfo.nintyCycleStart)}~${dateDisplay(headerInfo.nintyCycleEnd)})`
                : null}
            </div>
          </div>

          <div className="row">
            <div className="pt-hcol">
              <InterrogationStatus value={headerInfo.interrogationStatus} />
            </div>
            <PatientIcon
              id="anticoagulationTip"
              icon={anticoagulantIconInfo.anticoagulantIcon}
              text={anticoagulantIconInfo.anticoagulantText}
              color="red"
              name={`<p class="anticoagulationTip-message">ANTI-COAGULATION<br>${anticoagulantIconInfo.anticoagulantTooltip}</p>`}
              htmlContent
            />
            {headerInfo.patient.pacemakerDep === "YES" ? (
              <PatientIcon id="pacemakerdepTip" icon="faHeartbeat" color="red" name="PACEMAKER DEPENDENT" />
            ) : null}
            {headerInfo.patient.nameAlert ? (
              <PatientIcon id="nameAlertTip" icon="faExclamationTriangle" color="#ffc107" name="NAME ALERT" />
            ) : null}
          </div>
        </div>
        {headerInfo.interrogationStatus === "FINAL" ? (
          <label className="cursor-pointer" style={{ marginTop: 15, display: "flex", alignItems: "center" }}>
            <input
              className="tgl tgl-light"
              id="cb1"
              type="checkbox"
              ref={followUpCheckBox}
              checked={nurseFollowUp}
              onChange={() => {
                setNurseFollowUp(!nurseFollowUp);
              }}
              onClick={onFollowUpChange}
            />
            <label className="tgl-btn" htmlFor="cb1" style={{ display: "inline-block", marginRight: 10 }} />
            <span className="lbl-txt nurse-toggle-wrapper">
              Nurse Follow Up
              {nurseFollowUpLoading && (
                <div className="saving-teal-ico icon-64" style={{ marginLeft: 5, width: 20, height: 20 }} />
              )}
            </span>
          </label>
        ) : null}
      </div>
      <div className="col col-m-100 col-50" style={{ position: "relative" }}>
        <div className="m-mfr-data-sb" id="patient-device">
          <div className="row">
            <div className="pt-hcol">Device:</div>
            <div style={{ width: "65%" }} id="device-name">
              <b>
                {dv.deviceType} - {dv.manufacturerName}
              </b>{" "}
              ({dv.modelName}) SN:<b>{dv.serialNumber}</b>
            </div>
          </div>
          <div className="row">
            <div className="pt-hcol">Implant Date: </div>
            <div>
              <b id="device-implant-date">
                {dv.implantDate ? dayjs(dv.implantDate).format("MM/DD/YYYY") : "--"}
              </b>
            </div>
          </div>
          <div className="row">
            <div id="following-physician-label" className="pt-hcol">
              Following Physician:
            </div>
            <div>
              <b id="following-physician">{fp && `${fp.name} ${fp.family}`}</b>
            </div>
          </div>
          <div className="row">
            <div id="referring-physician-label" className="pt-hcol">
              Referring Physician:
            </div>
            <div>
              <b id="referring-physician">
                {referringPhysician &&
                  `${referringPhysician.name ? referringPhysician.name : ""} ${referringPhysician.family ? referringPhysician.family : ""
                  }`}
              </b>
            </div>
          </div>
          {disableAfterRegenerate ? (
            <div style={{ marginTop: "20px" }} id="regenerate-msg-label">
              The report has been successfully queued for regeneration, please check back in a few minutes.
            </div>
          ) : null}
        </div>
        {userInfo.rcc && userInfo.isServiceReport && (
          <div className="assigner-info col col-s-100">
            <AssignerInfo
              assignerName={assignerInfo?.user?.name}
              assignerFamily={assignerInfo?.user?.family}
              assignerDate={assignerInfo?.date}
            />
          </div>
        )}
      </div>
      <div className="col col-m-100 col-33">
        <div className="cmt-engage-pt hide">
          <div>
            <button className="btn btn-teal">
              <i className="ti ti-comment-alt"></i>
              <span>&nbsp;Comment</span>
            </button>
            <button className="btn btn-teal">
              <i className="ti ti-user"></i>
              <span>&nbsp;Engage Patient</span>
            </button>
          </div>
        </div>
      </div>
      {headerInfo.interrogationStatus === "FINAL" && showDownload ? (
        <button
          id="downloadReportBtnId"
          className="btn btn-teal"
          onClick={downloadReport}
          style={{ position: "absolute", right: 0 }}
          disabled={disableAfterRegenerate}
        >
          {downloadingPreReport ? (
            <img id="loading-spinner" src={savingspinner} style={{ width: 15 }}></img>
          ) : (
            <span style={{ width: 15 }}></span>
          )}
          Download Report
        </button>
      ) : null}
      {headerInfo.interrogationStatus === "FINAL" && showDownload ? (
        <button
          id="regenerateReportBtnId"
          className="btn btn-light"
          onClick={regenerateReport}
          style={{ position: "absolute", right: 0, top: "50px" }}
          disabled={disableAfterRegenerate || isLoading}
        >
          {isLoading ? (
            <div
              id="results-loading-icon-2"
              className="saving-white-ico icon-64"
              style={{ width: 15, height: 15, marginRight: 3 }}
            />
          ) : null}{" "}
          Regenerate
        </button>
      ) : null}
    </>
  );
};

export default UserInfoCard;
